import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, BarChart3, HelpCircle, CheckCircle2, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Aviso } from '@/components/ui/aviso';
import { EstadoError, EstadoVacio } from '@/components/ui/estado-vacio';
import { GrupoDeOpciones } from '@/components/ui/grupo-de-opciones';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { formatoCantidad, formatoPorcentaje } from '@/lib/utils/formato-precio';
import { describirVsMercado, POSICIONES, posicionDe } from '../utils/vs-mercado';
import { smartPricingApi } from '../services/smart-pricing-api';
import { NuevaEstrategiaDialog } from './nueva-estrategia/nueva-estrategia-dialog';
import { atributosSoloMira, useSoporteSoloMira } from '@/features/mini-prixia/soporte-solo-mira';
import type { BorradorDeEstrategia } from './nueva-estrategia/borrador';
import type { DesdeAnalisis } from './nueva-estrategia/paso-1-alcance';
import { usePuedeCrearEstrategias } from './nueva-estrategia/puede-crear';
import type { StrategyGroup, CpiPosition } from '../types/smart-pricing.types';

type GroupBy = 'cpiPosition' | 'category';

const GROUP_BY_OPTIONS: { valor: GroupBy; texto: string }[] = [
  { valor: 'cpiPosition', texto: 'Posición frente al mercado' },
  { valor: 'category', texto: 'Categoría' },
];

const CPI_ORDER: CpiPosition[] = POSICIONES.map((p) => p.clave);

// ── Helper dialog ─────────────────────────────────────────────────────────────

interface HelpStep {
  number: number;
  title: string;
  description: string;
  detail: string;
  tip?: string;
  /** `muestra`: la clase del token de la posición (`POSICIONES`). */
  indicators?: Array<{ muestra: string; label: string; meaning: string }>;
}

const HELP_STEPS: HelpStep[] = [
  {
    number: 1,
    title: 'Entendé cómo está cada segmento frente al mercado',
    description: 'Cuánto más caro o más barato está tu precio que el promedio de la competencia.',
    detail:
      '«0 %» significa que tu precio es igual al promedio de la competencia. En negativo estás más barato; en positivo, más caro. Entre −5 % y +5 % sos competitivo sin regalar margen.',
    indicators: [
      {
        muestra: 'bg-info',
        label: 'más de −15 %',
        meaning: 'Muy por debajo — posible margen perdido',
      },
      { muestra: 'bg-info/50', label: '−15 % a −5 %', meaning: 'Por debajo del mercado' },
      { muestra: 'bg-ok', label: '−5 % a +5 %', meaning: 'Alineado — zona ideal' },
      {
        muestra: 'bg-alerta',
        label: '+5 % a +20 %',
        meaning: 'Por encima — riesgo de pérdida de ventas',
      },
      {
        muestra: 'bg-bloqueo',
        label: 'más de +20 %',
        meaning: 'Muy caro frente al mercado — conviene revisarlo pronto',
      },
    ],
    tip: 'La tabla va ordenada como la escala: de muy bajo a muy alto.',
  },
  {
    number: 2,
    title: 'Leé la tabla de segmentos',
    description:
      'Cada fila junta productos con características parecidas. Revisá sus números antes de actuar.',
    detail:
      'Cada fila muestra cuántos productos tiene el segmento, cuánto están sus productos por encima o por debajo del mercado en promedio, el margen promedio sobre el precio y, si agrupás por categoría, cómo se reparten sus productos frente al mercado en una barra. Así ves el segmento entero antes de decidir.',
    tip: 'Podés agrupar por posición frente al mercado o por categoría con los botones de arriba.',
  },
  {
    number: 3,
    title: 'Creá una estrategia con esos productos',
    description:
      'Hacé clic en "Crear una estrategia con estos productos": se abre el asistente de tres pasos con esos productos ya cargados.',
    detail:
      'El asistente pregunta a quién alcanza, cómo calcula el precio (con lo que pasaría si hoy corriera) y cuándo revisa y con qué permiso. Lo que proponga llega a Por decidir, o se aplica solo si así lo elegiste.',
    tip: 'Crearla no cambia ningún precio: la estrategia propone, y el permiso que elijas decide si te pregunta antes de aplicar.',
  },
  {
    number: 4,
    title: 'Elegí cómo calcula',
    description:
      'En el segundo paso del asistente elegís el método. Cada uno dice qué ganás y qué perdés, y abajo ves qué pasaría si hoy corriera.',
    detail:
      'Hay cinco: Igualar al más barato, Quedar por debajo del más barato (por el % que digas), Apuntar a un margen, Precio inteligente (mira el mercado, la demanda y el stock) y Bajar un % de mi precio de lista (no necesita costo ni competencia). «Qué pasaría si hoy corriera» calcula con la estrategia sin crearla.',
    tip: 'Si tus productos están bastante por encima del mercado, «Igualar al más barato» suele ser el primer paso más seguro. Si están muy por debajo, «Apuntar a un margen» puede recuperar rentabilidad.',
  },
  {
    number: 5,
    title: 'Decidí cuándo y con qué permiso',
    description:
      'En el tercer paso elegís cada cuánto revisa, hasta cuándo corre y si te pregunta antes de cambiar un precio.',
    detail:
      'Con «Siempre preguntame» ningún precio se mueve sin tu sí: lo que proponga te espera en Por decidir. Si algunos productos ya tienen otra estrategia, el asistente te pide elegir qué hacer con ellos antes de crearla. Después de crearla la encontrás en la pestaña Estrategias.',
    tip: 'Podés cambiar el permiso después, desde el menú «Más» de la estrategia, con «Cambiar permiso».',
  },
];

/**
 * Nº 13 (D5): los pasos que describen el asistente (3, 4 y 5) no se muestran a
 * quien no puede crear: el botón al que mandan no está. El resto de la guía vale
 * igual. (El plan nombraba el 3 y el 4; con el texto de la T5, el 5 es el tercer
 * paso del asistente y también manda a crear.)
 */
export function pasosDeLaAyuda(puedeCrear: boolean): HelpStep[] {
  return puedeCrear ? HELP_STEPS : HELP_STEPS.filter((s) => s.number < 3);
}

function StrategyHelperDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [activeStep, setActiveStep] = useState(0);
  const pasos = pasosDeLaAyuda(usePuedeCrearEstrategias());
  const step = pasos[activeStep];

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          onClose();
          setActiveStep(0);
        }
      }}
    >
      <DialogContent className="max-w-3xl max-h-[88vh] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-4 border-b flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-primary" />
            Cómo usar Estrategias de Mercado
          </DialogTitle>
          <DialogDescription>
            Guía paso a paso para analizar segmentos y crear estrategias de precio con base en el
            mercado.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* Los pasos, a la izquierda */}
          <div className="w-56 border-r flex-shrink-0 overflow-y-auto py-3">
            {pasos.map((s, i) => (
              <button
                key={s.number}
                onClick={() => setActiveStep(i)}
                aria-current={activeStep === i ? 'step' : undefined}
                className={cn(
                  'w-full text-left px-4 py-3 flex items-start gap-3 transition-colors',
                  activeStep === i ? 'bg-primary/10' : 'hover:bg-muted/40'
                )}
              >
                <span
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold shrink-0 mt-0.5',
                    activeStep === i
                      ? 'bg-primary text-primary-foreground'
                      : i < activeStep
                        ? 'bg-primary/10 text-primary'
                        : 'bg-muted text-muted-foreground'
                  )}
                >
                  {i < activeStep ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <span
                  className={cn(
                    'text-xs font-medium leading-tight',
                    activeStep === i ? 'text-primary' : 'text-foreground/70'
                  )}
                >
                  {s.title}
                </span>
              </button>
            ))}
          </div>

          {/* El paso elegido */}
          <div className="flex-1 overflow-y-auto px-7 py-6 space-y-5">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold text-sm shrink-0">
                {activeStep + 1}
              </span>
              <div>
                <h3 className="font-semibold text-base leading-tight">{step.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
              </div>
            </div>

            <div className="rounded-lg border bg-muted/20 px-4 py-3.5">
              <p className="text-sm text-foreground/80 leading-relaxed">{step.detail}</p>
            </div>

            {step.indicators && (
              <div className="space-y-2">
                <p className="text-sm font-medium">La escala frente al mercado</p>
                <div className="space-y-2">
                  {step.indicators.map((ind) => (
                    <div key={ind.label} className="flex items-center gap-3">
                      <div className={cn('h-3 w-3 rounded-full shrink-0', ind.muestra)} />
                      <span className="text-xs font-semibold w-24 shrink-0">{ind.label}</span>
                      <span className="text-xs text-muted-foreground">{ind.meaning}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step.tip && <Aviso tono="info">{step.tip}</Aviso>}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t flex-shrink-0 flex items-center gap-2">
          <div className="flex-1 flex items-center gap-1">
            {pasos.map((s, i) => (
              <button
                key={s.number}
                aria-label={`Ir al paso ${i + 1}`}
                onClick={() => setActiveStep(i)}
                className={cn(
                  'h-1.5 rounded-full transition-[width] duration-300',
                  activeStep === i ? 'w-6 bg-primary' : 'w-1.5 bg-muted-foreground/30'
                )}
              />
            ))}
          </div>
          <Button
            variant="outline"
            onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
            disabled={activeStep === 0}
          >
            Anterior
          </Button>
          {activeStep < pasos.length - 1 ? (
            <Button className="gap-1.5" onClick={() => setActiveStep(activeStep + 1)}>
              Siguiente <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button className="gap-1.5" onClick={onClose}>
              <CheckCircle2 className="h-4 w-4" /> Entendido
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Cómo se reparten frente al mercado los productos de una categoría: una barra chica. */
function CpiDistBar({ dist, total }: { dist: Record<CpiPosition, number>; total: number }) {
  if (total === 0) return null;
  return (
    <div className="flex h-1.5 w-32 overflow-hidden rounded-full gap-px bg-muted">
      {POSICIONES.map((pos) => {
        const count = dist[pos.clave];
        if (count === 0) return null;
        return (
          <div
            key={pos.clave}
            className={cn('h-full', pos.muestra)}
            style={{ width: `${(count / total) * 100}%` }}
            title={`${pos.nombre}: ${formatoCantidad(count)}`}
          />
        );
      })}
    </div>
  );
}

/** «Alineado: 9 · Alto: 3»: lo mismo que la barra, en palabras. */
const repartoEnPalabras = (dist: Record<CpiPosition, number>) =>
  POSICIONES.filter((p) => dist[p.clave] > 0)
    .map((p) => `${p.nombre}: ${formatoCantidad(dist[p.clave])}`)
    .join(' · ');

// ── Contenido explicativo por tipo de agrupación ──────────────────────────────

const GROUP_BY_INFO: Record<GroupBy, { title: string; body: string }> = {
  cpiPosition: {
    title: 'Posición frente al mercado',
    body: 'Cuánto más caro o más barato está tu precio que el promedio de la competencia. «+12 %» quiere decir 12 % más caro que el mercado; «−8 %», 8 % más barato.',
  },
  category: {
    title: 'Categoría de producto',
    body: 'Agrupa según la categoría de cada producto de tu catálogo. Sirve para ver cómo se comporta el mercado por línea de negocio y encontrar categorías enteras fuera de precio.',
  },
};

/**
 * «Sin categoría» no es una categoría que se pueda elegir. El backend convierte
 * `__none__` en `'Sin categoría'` antes de mandarlo (smart-pricing.routes.ts:2282,
 * 27-sep B3): se miran las dos.
 */
const esSinCategoria = (g: StrategyGroup) => g.key === '__none__' || g.key === 'Sin categoría';

/**
 * El borrador con el que abre el asistente (P3-D4). Por categoría, la estrategia
 * alcanza a la CATEGORÍA (los que entren después también); por posición frente
 * al mercado no hay un «qué es» que la describa, así que va con esos productos.
 */
export function inicialDesdeElConjunto(
  g: StrategyGroup,
  agrupadoPor: GroupBy
): Partial<BorradorDeEstrategia> {
  if (agrupadoPor === 'category' && !esSinCategoria(g)) {
    return {
      nombre: `Estrategia para ${g.label}`,
      forma: 'CATEGORIA_Y_MARCA',
      categorias: [g.label],
    };
  }
  return {
    nombre: `Estrategia para ${g.label}`,
    forma: 'PRODUCTOS',
    skus: g.products.map((p) => p.sku),
  };
}

/**
 * D4 (27-sep): la tarjeta cuenta sólo los productos CON competencia (el agrupado
 * sale de los precios leídos, smart-pricing.routes.ts:2225) y la estrategia
 * alcanza a toda la categoría. El paso 1 lo avisa con el número de la tarjeta.
 */
export function desdeAnalisisDelConjunto(
  g: StrategyGroup,
  agrupadoPor: GroupBy
): DesdeAnalisis | null {
  return agrupadoPor === 'category' && !esSinCategoria(g) ? { enLaTarjeta: g.count } : null;
}

/**
 * Por posición, las filas van en el orden de la escala (de muy bajo a muy alto,
 * pulido spec 232); el backend las manda en el orden en que las encuentra. Por
 * categoría, como llegan.
 */
const enOrden = (groups: StrategyGroup[], groupBy: GroupBy) =>
  groupBy === 'cpiPosition'
    ? [...groups].sort(
        (a, b) => CPI_ORDER.indexOf(a.key as CpiPosition) - CPI_ORDER.indexOf(b.key as CpiPosition)
      )
    : groups;

export function StrategyGroupsView() {
  const [groupBy, setGroupBy] = useState<GroupBy>('cpiPosition');
  const [groups, setGroups] = useState<StrategyGroup[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showHelper, setShowHelper] = useState(false);
  const [infoDismissed, setInfoDismissed] = useState(false);

  // P3-D4 (plan 3): «crear una estrategia» abre el asistente de tres pasos con
  // estos productos cargados. Un solo camino: propuestas y permisos. El aplicado
  // directo (apply-strategy con su barra de progreso) se retiró de acá.
  const [nuevaDesde, setNuevaDesde] = useState<StrategyGroup | null>(null);
  // D5 (27-sep): crear pide un rol que pueda (el alta da 403 a los demás).
  const puedeCrear = usePuedeCrearEstrategias();
  // Soporte («Entrar como») en una demo sólo mira: no crea estrategias.
  const sinCambios = atributosSoloMira(useSoporteSoloMira());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await smartPricingApi.getStrategyGroups(groupBy);
      setGroups(res.groups);
      setTotal(res.total);
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudieron cargar los segmentos.'));
    } finally {
      setLoading(false);
    }
  }, [groupBy]);

  useEffect(() => {
    load();
  }, [load]);

  // Mostrar la explicación cada vez que el usuario cambia la agrupación
  useEffect(() => {
    setInfoDismissed(false);
  }, [groupBy]);

  const info = GROUP_BY_INFO[groupBy];
  const porCategoria = groupBy === 'category';

  return (
    <div className="space-y-4">
      <StrategyHelperDialog open={showHelper} onClose={() => setShowHelper(false)} />

      <NuevaEstrategiaDialog
        abierto={nuevaDesde !== null}
        onCerrar={() => setNuevaDesde(null)}
        // El asistente ya avisa «Estrategia creada» y dice dónde quedó: un segundo
        // aviso acá los apilaba (navegador T5).
        onCreada={() => setNuevaDesde(null)}
        inicial={nuevaDesde ? inicialDesdeElConjunto(nuevaDesde, groupBy) : null}
        desdeAnalisis={nuevaDesde ? desdeAnalisisDelConjunto(nuevaDesde, groupBy) : null}
      />

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Agrupar por:</span>
        <GrupoDeOpciones
          aria-label="Agrupar por"
          opciones={GROUP_BY_OPTIONS}
          valor={groupBy}
          onCambiar={setGroupBy}
        />
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="gap-1.5">
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          Actualizar
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setShowHelper(true)} className="gap-1.5">
          <HelpCircle className="h-3.5 w-3.5" />
          Cómo usar
        </Button>
        <p className="ml-auto text-sm text-muted-foreground">
          Total: <b className="font-semibold text-foreground">{formatoCantidad(total)}</b>{' '}
          {total === 1 ? 'producto' : 'productos'} con datos de competencia
        </p>
      </div>

      {/* La explicación de la agrupación elegida */}
      {!infoDismissed && (
        <Aviso tono="info" titulo={info.title} className="relative pr-9 text-sm">
          {info.body}
          <button
            type="button"
            aria-label="Cerrar la explicación"
            onClick={() => setInfoDismissed(true)}
            className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </Aviso>
      )}

      {error && <EstadoError onReintentar={() => void load()}>{error}</EstadoError>}

      {loading && groups.length === 0 && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-11 w-full" />
          ))}
        </div>
      )}

      {!loading && groups.length === 0 && !error && (
        <EstadoVacio titulo="Sin datos de mercado" icono={BarChart3}>
          Todavía no hay precios de la competencia leídos.
        </EstadoVacio>
      )}

      {groups.length > 0 && (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">
                  {porCategoria ? 'Categoría' : 'Posición frente al mercado'}
                </TableHead>
                <TableHead className="text-right text-xs">Productos</TableHead>
                <TableHead className="text-right text-xs">Frente al mercado</TableHead>
                <TableHead className="text-right text-xs">Margen prom. sobre el precio</TableHead>
                {porCategoria && <TableHead className="text-xs">Cómo se reparten</TableHead>}
                {puedeCrear && <TableHead />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {enOrden(groups, groupBy).map((group) => {
                const pos = porCategoria ? null : posicionDe(group.key);
                const vs = describirVsMercado(group.avgCpi);
                return (
                  <TableRow key={group.key}>
                    <TableCell>
                      {pos ? (
                        <>
                          <b className="font-semibold">{pos.nombre}</b>{' '}
                          <span className="text-muted-foreground">{pos.rango}</span>
                        </>
                      ) : (
                        <span className="font-semibold">{group.label}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatoCantidad(group.count)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={vs.frase}>
                      {vs.etiqueta}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {group.avgMargin !== null ? (
                        formatoPorcentaje(group.avgMargin)
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    {porCategoria && (
                      <TableCell>
                        <CpiDistBar dist={group.cpiDist} total={group.count} />
                        <div className="mt-1 text-xs text-muted-foreground">
                          {repartoEnPalabras(group.cpiDist)}
                        </div>
                      </TableCell>
                    )}
                    {puedeCrear && (
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setNuevaDesde(group)}
                          {...sinCambios}
                        >
                          Crear una estrategia con estos productos
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
